/* eslint-disable no-console */
/** ================== UTILS FOR DEVICE INFO ================== */

function detectBrowser(userAgent: string) {
  const browsers = [
    { regex: /edg\/([\d.]+)/i, name: 'Microsoft Edge' },
    {
      regex: /chrome\/([\d.]+)/i,
      name: 'Google Chrome',
      test: /chrome|crios|crmo/i,
    },
    {
      regex: /firefox\/([\d.]+)/i,
      name: 'Mozilla Firefox',
      test: /firefox|fxios/i,
    },
    { regex: /version\/([\d.]+)/i, name: 'Apple Safari', test: /safari/i },
    { regex: /opr\/([\d.]+)/i, name: 'Opera' },
  ];

  for (const browser of browsers) {
    if (
      browser.test
        ? browser.test.test(userAgent)
        : browser.regex.test(userAgent)
    ) {
      const match = userAgent.match(browser.regex);
      return {
        name: browser.name,
        version: match ? match[1] : 'Unknown',
      };
    }
  }

  return { name: 'Unknown', version: 'Unknown' };
}

function detectOS(userAgent: string) {
  const osList = [
    { regex: /Windows NT ([\d.]+)/i, name: 'Windows', test: /windows nt/i },
    {
      regex: /Mac OS X ([\d_]+)/i,
      name: 'macOS',
      test: /mac os x/i,
      format: (v: string) => v.replace(/_/g, '.'),
    },
    { regex: /Android ([\d.]+)/i, name: 'Android', test: /android/i },
    {
      regex: /OS ([\d_]+)/i,
      name: 'iOS',
      test: /iphone|ipad|ipod/i,
      format: (v: string) => v.replace(/_/g, '.'),
    },
    { regex: null, name: 'Linux', test: /linux/i },
  ];

  for (const os of osList) {
    if (os.test.test(userAgent)) {
      if (!os.regex) return { name: os.name, version: null };

      const match = userAgent.match(os.regex);
      const version = match ? match[1] : null;

      return {
        name: os.name,
        version: version && os.format ? os.format(version) : version,
      };
    }
  }

  return { name: 'Unknown', version: null };
}

/** Fallback used whenever the public IP can't be resolved in time. */
const UNKNOWN_IP = '0.0.0.0';
/** Max time to wait for the IP lookup before giving up (ms). */
const IP_FETCH_TIMEOUT = 2500;
/** Keeps the resolved IP stable across a hard reload within the same tab. */
const IP_STORAGE_KEY = 'sv_client_ip';

function readStoredIP(): string | null {
  try {
    return sessionStorage.getItem(IP_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeIP(ip: string): void {
  try {
    sessionStorage.setItem(IP_STORAGE_KEY, ip);
  } catch {
    // sessionStorage can be unavailable (private mode / blocked) — ignore.
  }
}

// The fingerprint runs in the request interceptor, so it is rebuilt for every
// API call. Its only volatile field is the IP, so the IP MUST be identical on
// every request — otherwise the login fingerprint and a later request's
// fingerprint disagree and the backend rejects it ("Fingerprint mismatch").
// We therefore resolve the IP exactly once and reuse that value for the whole
// session (even the 0.0.0.0 fallback), which also stops a slow/blocked ipify
// from stalling every request. `ipPromise` dedupes concurrent first calls.
let cachedIP: string | null = null;
let ipPromise: Promise<string> | null = null;

async function fetchIPAddress(): Promise<string> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IP_FETCH_TIMEOUT);
    const response = await fetch('https://api.ipify.org?format=json', {
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const data = await response.json();
    return data?.ip || UNKNOWN_IP;
  } catch (err) {
    console.warn('Failed to fetch IP:', err);
    return UNKNOWN_IP;
  }
}

/** Clears the cached IP. Test-only: lets each case exercise a fresh fetch. */
export function resetIPCache(): void {
  cachedIP = null;
  ipPromise = null;
  try {
    sessionStorage.removeItem(IP_STORAGE_KEY);
  } catch {
    // ignore
  }
}

async function getIPAddress(): Promise<string> {
  if (cachedIP) return cachedIP;

  // A value resolved earlier this tab session (even across a reload) must be
  // reused verbatim so the fingerprint stays byte-for-byte identical.
  const stored = readStoredIP();
  if (stored) {
    cachedIP = stored;
    return stored;
  }

  // Reuse an in-flight request so concurrent calls don't each hit the network.
  ipPromise ??= fetchIPAddress().then(ip => {
    // Cache the first resolved value unconditionally — including the 0.0.0.0
    // fallback — so every later request sends the SAME IP. Retrying on the
    // fallback would let a real IP appear mid-session and desync the
    // fingerprint from the one the backend recorded at login.
    cachedIP = ip;
    storeIP(ip);
    ipPromise = null;
    return ip;
  });
  return ipPromise;
}

/** ================== FINGERPRINT DATA COLLECTION ================== */

export interface FingerprintData {
  ipAddress: string;
  browserName: string;
  browserVersion?: string;
  osName?: string;
  osVersion?: string | null;
  userAgent: string;
  timezone?: string;
  platform?: string;
}

/**
 * Collects unencrypted fingerprint data
 * Backend will generate UUID and encrypt the data
 */
export async function getFingerprintData(): Promise<FingerprintData> {
  const userAgent = navigator.userAgent;
  const browser = detectBrowser(userAgent);
  const os = detectOS(userAgent);
  const ipAddress = await getIPAddress();

  return {
    ipAddress,
    browserName: browser.name,
    browserVersion: browser.version,
    osName: os.name,
    osVersion: os.version,
    userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform:
      (navigator as any).userAgentData?.platform ??
      navigator.platform ??
      'unknown',
  };
}
